/**
 * 层（专题《维护对象边界定义》）：规则 ／ 指南 ／ 知识 ／ 工具各带一个层标注，四页共用这一处——筛选项、徽标、抽屉里的「层」段。
 * 层只是管理标注，不影响运行；混合的（layer_notes 非空）写主层，notes 说明哪部分该拆到哪。
 */
import { Badge } from "@/components/ui/badge";
import type { PlanLayer } from "@/lib/api/plan-lab";

export const LAYER_NONE = "__none__";
const ALL = "__all__";

/** 接口没带层表时（旧接口 ／ 打桩数据）的兜底 */
export const LAYER_FALLBACK: PlanLayer[] = [
	{ id: "L1", name: "L1 泛行业", desc: "客服 Agent 的方法和机制，不依赖任何行业；随引擎发版" },
	{
		id: "L2-行业",
		name: "L2 行业语义",
		desc: "电商共同的业务概念（退货、未发货、行项）；随行业包发版",
	},
	{
		id: "L2-平台",
		name: "L2 平台与渠道",
		desc: "Shopify、WhatsApp 等的接口和形态差异（订单号写法、状态映射、卡片写法）",
	},
	{ id: "L2-语言", name: "L2 语言", desc: "按回复语言变、和行业无关（状态词、旁检词表）" },
	{ id: "L3", name: "L3 租户／店铺", desc: "这家店自己的政策、数值、说法；随租户配置变更" },
	{ id: "环境", name: "环境", desc: "演示数据、评测基建、基准环境，不是产品内容" },
];

export type Layered = { layer?: string; layer_notes?: string };

/** 筛选项：全部 ／ 各层 ／ 未标，括号里是条数；没有的层不列 */
export function layerOptions(layers: PlanLayer[], rows: Layered[]) {
	const n = (id: string) => rows.filter((r) => r.layer === id).length;
	const none = rows.filter((r) => !r.layer || !layers.some((l) => l.id === r.layer)).length;
	return [
		{ value: ALL, label: `全部（${rows.length}）` },
		...layers
			.filter((l) => n(l.id))
			.map((l) => ({ value: l.id, label: `${l.name}（${n(l.id)}）` })),
		...(none ? [{ value: LAYER_NONE, label: `未标（${none}）` }] : []),
	];
}

export function matchLayer(filter: string, row: Layered, layers: PlanLayer[]): boolean {
	if (filter === ALL) return true;
	if (filter === LAYER_NONE) return !row.layer || !layers.some((l) => l.id === row.layer);
	return row.layer === filter;
}

export function LayerBadge({
	row,
	layers,
}: {
	row: Layered;
	layers: PlanLayer[];
}) {
	const def = layers.find((l) => l.id === row.layer);
	if (!row.layer || !def)
		return (
			<span data-testid="plan-layer-badge">
				<Badge tone="warning">未标</Badge>
			</span>
		);
	const tip = [def.name, def.desc, row.layer_notes ? `混合：${row.layer_notes}` : ""]
		.filter(Boolean)
		.join("\n");
	return (
		<span title={tip} data-testid="plan-layer-badge">
			<Badge tone="muted">
				{row.layer}
				{row.layer_notes ? <span className="text-text-muted">·混合</span> : null}
			</Badge>
		</span>
	);
}

/** 抽屉里「层」段的内容：徽标 ＋ 这一层的说明 ＋ 混合时哪部分该拆到哪 */
export function LayerDetail({ row, layers }: { row: Layered; layers: PlanLayer[] }) {
	const def = layers.find((l) => l.id === row.layer);
	return (
		<div className="space-y-2" data-testid="plan-layer-detail">
			<div className="flex flex-wrap items-center gap-2">
				<LayerBadge row={row} layers={layers} />
				<span className="text-xs text-text-muted">{def?.desc ?? "这条内容还没有标层"}</span>
			</div>
			{row.layer_notes ? (
				<div className="space-y-1">
					<div className="text-xs text-text-muted">混合：哪部分该拆到哪</div>
					<div className="whitespace-pre-wrap text-sm text-text-primary">{row.layer_notes}</div>
				</div>
			) : null}
		</div>
	);
}

/** 筛选里「全部」的值（各页共用，免得各自再写一份） */
export const LAYER_ALL = ALL;
